import React, { useState, useEffect } from 'react';
import { getService } from '@Client/runner.hooks/useService';
import {
  CommentSource,
  CommentType,
  IComment
} from '@Shared.Angular/interfaces/comment.interface';
import CommentHistory from './CommentHistory/CommentHistory';
import CustomMention from './CustomMention/CustomMention';
import { ICommentMention } from '@Shared.Angular/interfaces/comment-mention.interface';
import { ActorType } from '@Shared.Angular/interfaces/actor-type.enum';
import FileProp from '@Client/runner.processmapsv2/@types/fileProp';
import CommentTargetType from '@Shared.Angular/@types/core/contracts/constants/commentTargetType';
import { Guid } from '@Shared.Angular/@types/guid';

export type Props = {
  commentTargetId: Guid;
  commentTargetType: number;
  commentAdded: boolean;
  onCommentsUpdate: (totalComments: number) => void;
};

const Feedback = (props: Props) => {
  const { commentTargetId, commentTargetType } = props;
  const [commentAdded, setCommentAdded] = useState(false);
  const [hasXssVulnerableString, setHasXssVulnerableString] = useState(false);
  const [everyoneMentioned, setEveryoneMentioned] = useState(false);
  const [comments, setComments] = useState<IComment[]>([]);
  const [filesPendingForUpload, setFilesPendingForUpload] = useState(false);
  const [lastAddedComment, setLastAddedComment] = useState('');
  const [fileList, setFileList] = useState([]);
  const [uploadedFileList, setUploadedFileList] = useState<FileProp[]>([]);
  const [newCommentText, setNewCommentText] = useState('');
  const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(false);
  const [flowOwnerOnly, setFlowOwnerOnly] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ICommentMention[]>([]);
  const sessionService = getService('sessionService');
  const fileService = getService('fileService');
  const tokenService = getService('tokenService');
  const notificationService = getService('notificationService');
  const pubSubService = getService('pubsubService');
  const flowinglyMentionService = getService('flowinglyMentionService');
  const validationService = getService('validationService');
  const commentApiService = getService('commentApiService');
  const appConfig = getService('APP_CONFIG');

  const handleFileChange = (event) => {
    const files = event.target.files;
    setFileList((prevFiles) => [...prevFiles, ...files]);
    setFilesPendingForUpload(true);
    pubSubService.publish('FILEUPLOAD_UPLOAD_STARTED', {});
    setIsAddButtonDisabled(true);
  };

  const uploadFiles = async () => {
    let hasException = false;
    const indexOfLastUploadedFile =
      uploadedFileList.length > 0 ? uploadedFileList.length : 0;
    const filesToUpload = fileList.slice(indexOfLastUploadedFile);
    let updatedFileList = uploadedFileList;
    const uploadPromises = filesToUpload.map(async (file) => {
      try {
        const response = await fileService.uploadCommentFile(file);
        const uploadedFile: FileProp = {
          filename: file.name,
          id: response.data,
          downloadLink: fileService.getDownloadLink(response.data),
          size: Math.floor(file.size / 1000)
        };
        updatedFileList = [...updatedFileList, uploadedFile];
        updatedFileList.sort((a, b) => a.filename.localeCompare(b.filename));
        pubSubService.publish('FILEUPLOAD_UPLOAD_COMPLETED', {});
        notificationService.showSuccessToast(file.name + ' uploaded');
        return { success: true, file };
      } catch (error) {
        return { success: false, reason: error, file };
      }
    });
    const results = await Promise.all(uploadPromises);
    results.forEach((result) => {
      if (result.success !== true) {
        hasException = true;
        const fileIndex = fileList.findIndex(
          (f) => f.name === result.file.name
        );
        if (fileIndex > -1) {
          fileList.splice(fileIndex, 1);
        }
      }
    });
    setUploadedFileList(updatedFileList);
    setFilesPendingForUpload(false);
    setIsAddButtonDisabled(false);
    if (hasException) {
      pubSubService.publish('FILEUPLOAD_UPLOAD_FAILED', {});
    }
  };

  const downloadFile = async (event: React.MouseEvent, fileId: string) => {
    event.preventDefault();
    fileService.downloadById(fileId);
  };

  const addComment = async () => {
    if (!newCommentText.trim()) {
      const warningText = 'Cannot add empty feedback';
      notificationService.showWarningToast(warningText);
      return;
    }

    const resultString = await Promise.all(
      selectedUser.map(async (user) => {
        const result = await flowinglyMentionService.transformMentionForDisplay(
          user
        );
        return result;
      })
    ).then((results) => results.join(' '));

    const mentions = flowinglyMentionService.extractMentionsFromCommentText(
      resultString || ''
    );

    const commentContents = transformMentionDisplayToStore(
      newCommentText || ''
    );

    const isFlowOrStepTaskComment =
      commentTargetType === CommentTargetType.FLOW ||
      commentTargetType === CommentTargetType.STEP_TASK;

    const confirmText = isFlowOrStepTaskComment
      ? 'Comment added'
      : 'Feedback added';
    const warningText = isFlowOrStepTaskComment
      ? 'Cannot add empty comment'
      : 'Cannot add empty feedback';

    if (flowinglyMentionService.trimSpaces(commentContents) === '') {
      notificationService.showWarningToast(warningText);
      return;
    } else if (validationService.isXssVulnerableString(commentContents)) {
      notificationService.showErrorToast(
        'The comment field contains invalid text. Please remove any HTML or JavaScript text from the field.'
      );
      return;
    }

    const comment: IComment = {
      comment: commentContents,
      mentions: mentions,
      fileIds: uploadedFileList.map((f) => f.id),
      userId: sessionService.getUser().id,
      flowOwnerOnly: flowOwnerOnly,
      commentType: CommentType.UserComment,
      source: CommentSource.Runner,
      flowModelVersion: ''
    };

    try {
      await commentApiService.addFlowComment(
        commentTargetType,
        commentTargetId,
        comment
      );
      setLastAddedComment(comment.comment);
      setFileList([]);
      setUploadedFileList([]);
      notificationService.showSuccessToast(confirmText);
    } catch (error) {
      console.log('Failed to add comment');
    }
    setEveryoneMentioned(false);
    setNewCommentText('');
    setSelectedUser([]);
    setCommentAdded(true);
  };

  const transformMentionDisplayToStore = (text: string) => {
    const mentionRegex = /@\[\|([^|]+)\|\]@/g;
    const lineBreakRegex = /\n/g;

    const replacedText = text
      .replace(mentionRegex, (_, mention) => {
        return `[~${mention}]`;
      })
      .replace(lineBreakRegex, '<br>');

    return replacedText;
  };

  const removeFile = async (originalIndex: number) => {
    try {
      if (originalIndex === -1) {
        return;
      }
      setIsAddButtonDisabled(true);
      const file = uploadedFileList[originalIndex];
      const updatedFileList = [...fileList];
      const updatedUploadedFileList = uploadedFileList.filter(
        (_, index) => index !== originalIndex
      );
      setUploadedFileList(updatedUploadedFileList);
      updatedFileList.splice(originalIndex, 1);
      setFileList(updatedFileList);

      const user = sessionService.getUser();
      fileService.setUser(user.id, tokenService.getTenant().id);
      await fileService.removeFileForId(file.id);
      notificationService.showSuccessToast(file.filename + ' Removed');
    } catch (error) {
      setIsAddButtonDisabled(false);
    } finally {
      setIsAddButtonDisabled(false);
    }
  };

  const removeAllFiles = async () => {
    if (!fileList || fileList.length === 0) return;
    try {
      const fileIds = fileList.map((f) => f.id);
      await Promise.all(fileIds.map((id) => fileService.removeFileForId(id)));
      setFileList([]);
      setUploadedFileList([]);
    } catch (error) {
      return;
    }
  };

  const isFlowOwnerOnly = () => {
    return appConfig.flowModelFeedbackVisibility.toLowerCase() === 'flowowners';
  };

  const handleValueChange = (value: string) => {
    const isXssVulnerable = validationService.isXssVulnerableString(value);
    setHasXssVulnerableString(isXssVulnerable);
    if (commentAdded) {
      setCommentAdded(false);
    }
    setNewCommentText(value);
  };

  const handleSelectedMentions = (value: ICommentMention[]) => {
    const isEveryoneMentioned = value.find(
      (item) =>
        item.actorName === 'Everyone' && item.actorTypeId === ActorType.Team
    );
    setSelectedUser(value);
    isEveryoneMentioned
      ? setEveryoneMentioned(true)
      : setEveryoneMentioned(false);
  };

  useEffect(() => {
    try {
      if (commentTargetType == null || !commentTargetId) {
        return;
      }
      const getComments = async () => {
        const data: IComment[] = await commentApiService.getFlowComments(
          commentTargetType,
          commentTargetId,
          false
        );

        const filteredData = data.filter(
          (comment) => comment.source !== CommentSource.Modeler
        );

        setComments(filteredData);
        props.onCommentsUpdate(filteredData?.length);
      };
      getComments();
    } catch (error) {
      return;
    }
  }, [commentTargetType, commentTargetId, lastAddedComment]);

  useEffect(() => {
    if (filesPendingForUpload) {
      uploadFiles();
    }
  }, [filesPendingForUpload]);

  useEffect(() => {
    const flowOwner = isFlowOwnerOnly();
    setFlowOwnerOnly(flowOwner);
    const handleCompleteStep = () => {
      removeAllFiles();
    };
    pubSubService.subscribe(
      'SIGNALR_RUNNER_COMPLETE_STEP',
      handleCompleteStep,
      'runner.processmapsv2-feedback-comment'
    );

    return () => {
      pubSubService.unsubscribeAll('runner.processmapsv2-feedback-comment');
    };
  }, []);

  return (
    <div className={'v2-view-feedback-comments-container'}>
      <CommentHistory comments={comments} />
      <div className={'v2-view-feedback-add-comment-container'}>
        <div className={'v2-view-feedback-add-comment-container-inner'}>
          <div className={'v2-view-feedback-add-comment-header'}>
            Add feedback
          </div>
          <CustomMention
            flowOwnerOnly={flowOwnerOnly}
            commentTargetId={commentTargetId}
            commentAdded={commentAdded}
            onValueChange={handleValueChange}
            mentionedUsers={handleSelectedMentions}
          />
          {hasXssVulnerableString && (
            <div className="mention-contents-error">
              This input does not support HTML.
            </div>
          )}
          {everyoneMentioned && (
            <div className="mention-contents-error">
              Mentioning "Everyone" will notify every single User in this
              Business!
            </div>
          )}
          <div className={'v2-view-feedback-add-comment-action'}>
            <div
              className={'v2-view-feedback-add-comment-foot-text'}
              style={{ minWidth: '0%' }}
            >
              <div className={'v2-view-feedback-add-comment-attach-file'}>
                <label htmlFor={`addCommentFile_${props.commentTargetId}`}>
                  <i className="fa-light fa-paperclip fa-lg mr-5"></i>
                  <span className="secondary-text">Attach File</span>
                  <input
                    id={`addCommentFile_${props.commentTargetId}`}
                    style={{ display: 'none' }}
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    onClick={(event) => {
                      (event.target as HTMLInputElement).value = null;
                    }}
                  />
                </label>
                <ul className={'file-list'}>
                  {uploadedFileList.map((file, index) => (
                    <li key={index}>
                      <a
                        href={file.downloadLink}
                        onClick={(e) => downloadFile(e, file.id)}
                      >
                        {file.filename}
                      </a>
                      <span>({file.size}K)</span>
                      <i
                        onClick={() => removeFile(index)}
                        title="remove"
                        className={'fa-light fa-xmark'}
                      ></i>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={'secondary-text mt-10'}>
                To mention a user or team, type '@' followed by the name
              </div>
            </div>
            <button
              className={'right btn green'}
              disabled={isAddButtonDisabled}
              onClick={addComment}
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Feedback;
